/**
 * Utilitário para normalização de valores de contração
 * Remove valores problemáticos como "n/a" e barras "/" antes do envio para o backend
 */

/**
 * Normaliza um valor de contração removendo caracteres problemáticos
 * @param contraction Valor de contração a ser normalizado
 * @returns Valor normalizado ou '0px' como fallback
 */
export function normalizeContraction(contraction: string | number | null | undefined): string {
  if (contraction === null || contraction === undefined) {
    return '0px';
  }

  // Converter para string se necessário
  let normalized = String(contraction).trim();

  // Remover valores problemáticos
  if (normalized === '' || 
      normalized.toLowerCase() === 'n/a' || 
      normalized === 'null' || 
      normalized === 'undefined') {
    return '0px';
  }

  // Remover barras "/" do valor
  normalized = normalized.replace(/\//g, '');

  // Se ficou vazio após remover barras, retornar 0px
  if (normalized === '') {
    return '0px';
  }

  // Validar se é um valor numérico válido
  if (normalized === 'AUTO') {
    return 'AUTO';
  }

  // Se contém 'px', manter como está (após limpeza)
  if (normalized.includes('px')) {
    return normalized;
  }

  // Se é um número puro, adicionar 'px'
  const numericValue = parseFloat(normalized);
  if (!isNaN(numericValue) && isFinite(numericValue)) {
    return `${numericValue}px`;
  }

  // Fallback para valores inválidos
  return '0px';
}

/**
 * Converte contração normalizada para número (para uso em APIs)
 * @param contraction Valor de contração normalizado
 * @returns Número representando a contração (0-100)
 */
export function contractionToNumber(contraction: string): number {
  const normalized = normalizeContraction(contraction);
  
  if (normalized === 'AUTO') {
    return 3.5;
  }
  
  if (normalized.includes('px')) {
    const value = parseFloat(normalized.replace('px', ''));
    return isNaN(value) ? 0 : value;
  }
  
  const value = parseFloat(normalized);
  return isNaN(value) ? 0 : value;
}

/**
 * Valida se um valor de contração é válido
 * @param contraction Valor a ser validado
 * @returns true se o valor é válido
 */
export function isValidContraction(contraction: string | number | null | undefined): boolean {
  const normalized = normalizeContraction(contraction);
  return normalized !== '0px' || contraction === '0px' || contraction === 0;
}
